import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart, Line, ComposedChart, Area, AreaChart, RadialBarChart, RadialBar } from 'recharts';
import { useOperationalStats } from '../../hooks/useOperationalStats';
import LoadingSpinner from '../common/LoadingSpinner';
import StatsCard from '../common/StatsCard';
import { formatCurrency, formatNumber } from '../../utils/formatters';

interface Props {
  dateRange: { start_date?: string; end_date?: string };
}

export default function OperationalTab({ dateRange }: Props) {
  const { data: stats, isLoading, error } = useOperationalStats(dateRange);

  if (isLoading) return <LoadingSpinner />;
  if (error) return <div className="text-red-500">Erreur lors du chargement des données</div>;
  if (!stats) return null;

  // Données d'efficacité opérationnelle
  const efficiencyData = stats.daily_efficiency.map(day => ({
    date: new Date(day.date).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit' }),
    patients_planned: day.appointments_scheduled,
    patients_seen: day.appointments_completed,
    efficiency_rate: (day.appointments_completed / day.appointments_scheduled * 100),
    average_duration: day.avg_appointment_duration,
    revenue_per_hour: day.revenue_per_hour
  }));

  // Données de performance par praticien
  const doctorPerformance = stats.doctor_performance.map(doctor => ({
    name: doctor.doctor_name,
    punctuality: doctor.punctuality_rate,
    patient_satisfaction: doctor.satisfaction_score,
    revenue_per_day: doctor.daily_revenue,
    treatments_per_day: doctor.daily_treatments
  }));

  return (
    <div className="space-y-6">
      {/* KPIs Opérationnels */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          title="Taux d'Occupation"
          value={`${stats.capacity_utilization.toFixed(1)}%`}
          icon="🏥"
          color={stats.capacity_utilization >= 85 ? "green" : stats.capacity_utilization >= 70 ? "yellow" : "red"}
        />
        <StatsCard
          title="Ponctualité Moyenne"
          value={`${stats.avg_punctuality.toFixed(1)}%`}
          icon="⏰"
          color={stats.avg_punctuality >= 90 ? "green" : "yellow"}
        />
        <StatsCard
          title="Temps Attente Moy."
          value={`${stats.avg_waiting_time} min`}
          icon="⏱️"
          color={stats.avg_waiting_time <= 15 ? "green" : stats.avg_waiting_time <= 30 ? "yellow" : "red"}
        />
        <StatsCard
          title="Satisfaction Patient"
          value={`${stats.patient_satisfaction.toFixed(1)}/10`}
          icon="😊"
          color={stats.patient_satisfaction >= 8 ? "green" : stats.patient_satisfaction >= 6 ? "yellow" : "red"}
        />
      </div>

      {/* Efficacité quotidienne */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Efficacité Opérationnelle Quotidienne</h3>
        <ComposedChart width={1000} height={400} data={efficiencyData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis yAxisId="left" />
          <YAxis yAxisId="right" orientation="right" />
          <Tooltip formatter={(value, name) => [
            name === 'revenue_per_hour' ? formatCurrency(Number(value)) :
            name === 'efficiency_rate' ? `${value}%` :
            name === 'average_duration' ? `${value} min` : formatNumber(Number(value)),
            name === 'patients_planned' ? 'Patients Prévus' :
            name === 'patients_seen' ? 'Patients Vus' :
            name === 'efficiency_rate' ? 'Taux Efficacité' :
            name === 'average_duration' ? 'Durée Moy.' : 'Revenus/Heure'
          ]} />
          <Legend />
          <Bar yAxisId="left" dataKey="patients_planned" fill="#e5e7eb" name="Patients Prévus" />
          <Bar yAxisId="left" dataKey="patients_seen" fill="#3b82f6" name="Patients Vus" />
          <Line yAxisId="right" type="monotone" dataKey="efficiency_rate" stroke="#10b981" strokeWidth={2} name="Taux Efficacité" />
          <Line yAxisId="right" type="monotone" dataKey="revenue_per_hour" stroke="#f59e0b" strokeWidth={2} name="Revenus/Heure" />
        </ComposedChart>
      </div>

      {/* Performance des praticiens */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Performance des Praticiens</h3>
          <RadialBarChart width={500} height={350} data={doctorPerformance}>
            <RadialBar
              label={{ position: 'insideStart', fill: '#fff' }}
              background
              dataKey="patient_satisfaction"
              fill="#8884d8"
            />
            <Legend iconSize={10} layout="horizontal" verticalAlign="bottom" />
            <Tooltip formatter={(value) => [`${value}/10`, 'Satisfaction']} />
          </RadialBarChart>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Productivité vs Qualité</h3>
          <BarChart width={500} height={350} data={doctorPerformance}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
            <YAxis />
            <Tooltip formatter={(value, name) => [
              name === 'revenue_per_day' ? formatCurrency(Number(value)) : value,
              name === 'treatments_per_day' ? 'Traitements/Jour' : 'Revenus/Jour'
            ]} />
            <Legend />
            <Bar dataKey="treatments_per_day" fill="#82ca9d" name="Traitements/Jour" />
            <Bar dataKey="revenue_per_day" fill="#8884d8" name="Revenus/Jour" />
          </BarChart>
        </div>
      </div>

      {/* Gestion des stocks et équipements */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">État des Stocks Critiques</h3>
          <AreaChart width={500} height={300} data={stats.inventory_status}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="item_category" />
            <YAxis />
            <Tooltip />
            <Area type="monotone" dataKey="current_stock" stackId="1" stroke="#82ca9d" fill="#82ca9d" />
            <Area type="monotone" dataKey="minimum_required" stackId="2" stroke="#ff7300" fill="#ff7300" />
          </AreaChart>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Maintenance Équipements</h3>
          <div className="space-y-4">
            {stats.equipment_maintenance.map((equipment, index: number) => (
              <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">{equipment.equipment_name}</h4>
                  <p className="text-sm text-gray-500">Dernière maintenance: {equipment.last_maintenance}</p>
                </div>
                <div className="text-right">
                  <span className={`px-2 py-1 text-xs rounded ${
                    equipment.status === 'optimal' ? 'bg-green-100 text-green-800' :
                    equipment.status === 'maintenance_due' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {equipment.status === 'optimal' ? 'Optimal' :
                     equipment.status === 'maintenance_due' ? 'Maintenance Due' : 'Critique'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Analyses de flux patients */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Flux Patients par Heure</h3>
        <LineChart width={1000} height={300} data={stats.patient_flow_hourly}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="hour" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="arrivals" stroke="#8884d8" strokeWidth={2} name="Arrivées" />
          <Line type="monotone" dataKey="in_treatment" stroke="#82ca9d" strokeWidth={2} name="En Soins" />
          <Line type="monotone" dataKey="waiting" stroke="#ff7300" strokeWidth={2} name="En Attente" />
        </LineChart>
      </div>

      {/* Recommandations opérationnelles */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">⚡ Optimisations Recommandées</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 border border-gray-200 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">🕐 Gestion du Temps</h4>
            <p className="text-sm text-gray-600">
              Réduire le temps d'attente en optimisant les créneaux de {stats.peak_hours.join(', ')}h.
            </p>
          </div>
          <div className="p-4 border border-gray-200 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">📦 Stocks</h4>
            <p className="text-sm text-gray-600">
              {stats.low_stock_items} articles en rupture. Réviser les seuils de réapprovisionnement.
            </p>
          </div>
          <div className="p-4 border border-gray-200 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">👨‍⚕️ Formation</h4>
            <p className="text-sm text-gray-600">
              Formations recommandées pour améliorer la satisfaction patient.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}